import { APIEndpoints } from "@/constants/apiEndpoints";
import { ENV_VARS } from "@/constants/env";
import { getAuthTokensFromLocalStorage } from "@/utils/auth";
import { languageHeader } from "@/i18n/request-language";

export type AiPdfPart = "full" | "table" | "chart";

const extractFilename = (header: string | null, fallback: string): string => {
  if (!header) return fallback;
  const match = header.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
};

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

interface DownloadArgs {
  messageId: number;
  part?: AiPdfPart;
  // 0-based index of the table/chart within the message (source order).
  index?: number;
  fallbackName?: string;
}

/**
 * Fetches a server-generated PDF for one AI assistant message — the whole
 * message (part="full") or a single table/chart inside it (part="table" |
 * "chart" + index) — and triggers the browser download. Backend renders the
 * same markdown the chat does, so the PDF matches the on-screen bubble.
 */
export const downloadAiMessagePdf = async ({
  messageId,
  part = "full",
  index = 0,
  fallbackName,
}: DownloadArgs): Promise<void> => {
  const tokens = getAuthTokensFromLocalStorage();
  if (!tokens?.accessToken) throw new Error("Not authenticated");

  const baseUrl = ENV_VARS.API_INSTANCE.replace(/\/$/, "");
  const path = APIEndpoints.MySpace.AIAssistant.DownloadMessagePdf(messageId);
  const qs = new URLSearchParams({ part, index: String(index) });

  const res = await fetch(`${baseUrl}${path}?${qs.toString()}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${tokens.accessToken}`,
      ...languageHeader(),
    },
  });

  if (!res.ok) {
    let message = `Failed to generate PDF (${res.status})`;
    try {
      const data = await res.json();
      if (data?.message) message = data.message;
    } catch {
      // response was not JSON
    }
    throw new Error(message);
  }

  const blob = await res.blob();
  const name = extractFilename(
    res.headers.get("content-disposition"),
    fallbackName ?? `ai-assistant-${messageId}.pdf`,
  );
  triggerBlobDownload(blob, name);
};
